import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
  LogoutButton?: ReactNode;
  showNavigationMenu?: boolean;
  onToggleSidebar?: () => void;
  height?: string;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  LogoutButton,
  Secondary,
  showNavigationMenu = false,
  onToggleSidebar,
  height = 'h-[48px]',
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className={`relative ${height} items-center`}>
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
            {showNavigationMenu && onToggleSidebar && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleSidebar}
                className="text-primary hover:bg-primary-dark ml-2 mr-2 h-8 w-8"
              >
                <Icons.HamburgerMenu className="h-6 w-6" />
              </Button>
            )}
            <div
              className={classNames(
                'mr-3 inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
              <div className="ml-1">
                {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Icons.IQLineLogo />}
              </div>
            </div>
          </div>
          <div className="absolute top-1/2 left-[250px] h-8 -translate-y-1/2">{Secondary}</div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            {children}
          </div>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center">
            {UndoRedo}
            {UndoRedo && <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>}
            {PatientInfo}
            {PatientInfo && (menuOptions.length > 0 || LogoutButton) && (
              <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
            )}
            {LogoutButton}
            {menuOptions.length > 0 && (
              <div className="flex-shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-primary hover:bg-primary-dark mt-2 h-full w-full"
                    >
                      <Icons.GearSettings />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {menuOptions.map((option, index) => {
                      const IconComponent = option.icon
                        ? Icons[option.icon as keyof typeof Icons]
                        : null;
                      return (
                        <DropdownMenuItem
                          key={index}
                          onSelect={option.onClick}
                          className="flex items-center gap-2 py-2"
                        >
                          {IconComponent && option.icon && (
                            <span className="flex h-4 w-4 items-center justify-center">
                              <Icons.ByName name={option.icon} />
                            </span>
                          )}
                          <span className="flex-1">{option.title}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
